import React, { useState, useEffect } from "react";

/* ─── DATA ─────────────────────────────────────────────────────── */
const POEMS = {
  robotics: [
    `Gears align with thought's design,\nI shape the world with hand and mind.\nCircuits spark with silent grace,\nMy code gives limbs their rightful place.`,
    `In Outer Majad's metal womb,\nI breathe in life from lifeless bloom.\nThrough logic deep and wires tight,\nI forge new beings into light.`,
    `Beyond the gears, a vision grows,\nWhere steel and soul together flows.\nMy craft's not bound to bolts alone,\nBut to the dreams that I've made my own.`,
  ],
  violin: [
    `Strings that cry beneath the bow,\nSpeak all the truths I cannot show.\nA single note, a soul undone,\nMy heart is played till silence won.`,
    `In Inner Majad's sacred air,\nI draw out peace from whispered prayer.\nWith trembling hand and open chest,\nI summon calm and cosmic rest.`,
    `And as the echoes fade away,\nThe music's ghost decides to stay.\nIt haunts the strings, it warms the room,\nA timeless flame in endless bloom.`,
  ],
  racing: [
    `Wheels that roar and engines scream,\nI chase the edge of every dream.\nTime bends back, the world turns thin,\nI ride the fire that burns within.`,
    `Through Majad's path of winding flame,\nI race not for the gold or fame.\nBut for the thrill that breaks the chain,\nWhere motion speaks, and thoughts remain.`,
    `Each turn's a risk, each straight a flight,\nMy shadow fades into the night.\nThe road's my canvas, speed my art,\nAnd freedom burns within my heart.`,
  ],
};

const MODAL_POEM = `"Beyond the stars where silence sings,
I stand in Outer Majad's rings.
The sky is still, the void is wide
Yet I can hear what's locked inside.

In hidden breath, the echoes call,
From Inner Majad's sacred hall.
Where secrets drift in endless flight,
Wrapped in dream and ancient light.

At time foretold one eighty flame,
Let veils be torn, reveal true name.
Open the rift, obey the sign
Let Inner Majad now align."`;

const SNOWFLAKES = [
  { left: "4%",  top: "8%",  delay: "0s",   size: "1.3rem", dur: "6s"  },
  { left: "90%", top: "12%", delay: "0.7s", size: "0.9rem", dur: "8s"  },
  { left: "2%",  top: "55%", delay: "1.5s", size: "1.1rem", dur: "7s"  },
  { left: "95%", top: "60%", delay: "0.3s", size: "0.8rem", dur: "9s"  },
  { left: "48%", top: "3%",  delay: "2s",   size: "0.75rem",dur: "6.5s"},
  { left: "18%", top: "92%", delay: "1s",   size: "1rem",   dur: "7.5s"},
  { left: "80%", top: "88%", delay: "3s",   size: "1.2rem", dur: "8.5s"},
  { left: "65%", top: "5%",  delay: "1.8s", size: "0.7rem", dur: "5.5s"},
];

/* ─── STYLES ────────────────────────────────────────────────────── */
const css = `
  @import url('https://fonts.googleapis.com/css2?family=Cinzel:wght@400;600;700;900&family=Raleway:wght@300;400;500;600&display=swap');

  *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }

  :root {
    --ice0:  #040d18;
    --ice1:  #071828;
    --ice2:  #0d2a45;
    --ice3:  #0a3a5c;
    --cyan:  #00D4FF;
    --cyanD: #1A7FC1;
    --cyanL: #A8D8F0;
    --frost: rgba(0,212,255,0.12);
    --frostB: rgba(0,212,255,0.25);
    --white: #e8f6ff;
    --muted: rgba(168,216,240,0.55);
  }

  html { scroll-behavior: smooth; }

  body {
    font-family: 'Raleway', sans-serif;
    background: var(--ice0);
    color: var(--white);
    overflow-x: hidden;
  }

  /* ── scrollbar ── */
  ::-webkit-scrollbar { width: 4px; }
  ::-webkit-scrollbar-track { background: var(--ice0); }
  ::-webkit-scrollbar-thumb { background: var(--cyanD); border-radius: 2px; }

  /* ── keyframes ── */
  @keyframes bgShift {
    0%,100% { background-position: 0% 50%; }
    50%      { background-position: 100% 50%; }
  }
  @keyframes pulseRing {
    0%,100% { transform: translate(-50%,-50%) scale(.88); opacity: .5; }
    50%      { transform: translate(-50%,-50%) scale(1.08); opacity: 1; }
  }
  @keyframes glowLoop {
    0%,100% { opacity:.08; filter:brightness(70%) drop-shadow(0 0 4px rgba(0,212,255,.3)); }
    50%      { opacity:1;   filter:brightness(130%) drop-shadow(0 0 28px #00D4FF) drop-shadow(0 0 55px rgba(0,180,255,.6)); }
  }
  @keyframes showFade {
    0%  { opacity:0; }
    60% { opacity:1;   filter:drop-shadow(0 0 24px #00D4FF) drop-shadow(0 0 50px rgba(0,180,255,.5)); }
    100%{ opacity:.22; filter:grayscale(100%) brightness(40%) contrast(120%); }
  }
  @keyframes floatFlake {
    0%,100% { transform:translateY(0) rotate(0deg);   opacity:.45; }
    50%      { transform:translateY(-16px) rotate(180deg); opacity:1; }
  }
  @keyframes shimBar {
    0%   { width:0%;   opacity:.4; }
    70%  { opacity:1; }
    95%  { width:92%;  opacity:.9; }
    100% { width:100%; opacity:1; }
  }
  @keyframes fadeUp {
    from { opacity:0; transform:translateY(28px); }
    to   { opacity:1; transform:translateY(0); }
  }
  @keyframes iceGlow {
    0%,100% { box-shadow: 0 0 15px rgba(0,212,255,.15), inset 0 0 20px rgba(0,212,255,.05); }
    50%      { box-shadow: 0 0 35px rgba(0,212,255,.35), inset 0 0 35px rgba(0,212,255,.10); }
  }
  @keyframes titlePulse {
    0%,100% { text-shadow: 0 0 20px rgba(0,212,255,.7), 0 0 50px rgba(0,212,255,.3); }
    50%      { text-shadow: 0 0 30px rgba(0,212,255,1), 0 0 70px rgba(0,212,255,.6), 0 0 120px rgba(0,212,255,.2); }
  }
  @keyframes snowDrift {
    from { top: -30px; }
    to   { top: 110vh; }
  }
  @keyframes runeFlicker {
    0%,100% { opacity:.08; }
    50%      { opacity:.18; }
  }
  @keyframes modalIn {
    from { transform:scale(.88) translateY(20px); opacity:0; }
    to   { transform:scale(1)   translateY(0);    opacity:1; }
  }
  @keyframes lineExpand {
    0%  { width:50px; opacity:.4; }
    100%{ width:110px; opacity:1; }
  }
  @keyframes iceLine {
    0%,100% { opacity:.3; }
    50%      { opacity:.8; }
  }

  /* ── NAVBAR ── */
  .nav {
    position: fixed; top:0; left:0; right:0; z-index: 100;
    display: flex; align-items: center; justify-content: space-between;
    padding: .9rem 2.5rem;
    background: rgba(4,13,24,.6);
    backdrop-filter: blur(16px);
    border-bottom: 1px solid rgba(0,212,255,.15);
    box-shadow: 0 0 30px rgba(0,212,255,.08);
    animation: iceGlow 6s ease-in-out infinite;
  }
  .nav-logo {
    font-family:'Cinzel',serif; font-size:1.3rem; font-weight:700;
    color: var(--cyan); letter-spacing:.2em;
    text-shadow: 0 0 12px rgba(0,212,255,.7);
    text-decoration:none;
  }
  .nav-links { display:flex; gap:2rem; list-style:none; }
  .nav-links a {
    font-size:.82rem; font-weight:500; letter-spacing:.15em; text-transform:uppercase;
    color: var(--cyanL); text-decoration:none;
    transition: color .3s, text-shadow .3s;
  }
  .nav-links a:hover {
    color:#fff;
    text-shadow: 0 0 12px rgba(0,212,255,.9);
  }
  .nav-burger { display:none; flex-direction:column; gap:5px; cursor:pointer; }
  .nav-burger span {
    width:22px; height:2px; background:var(--cyanL); border-radius:1px;
    transition:all .3s;
  }
  @media(max-width:640px){
    .nav-links { display:none; }
    .nav-links.open {
      display:flex; flex-direction:column; gap:1.2rem;
      position:fixed; top:56px; left:0; right:0;
      background:rgba(4,13,24,.95); backdrop-filter:blur(20px);
      padding:1.5rem 2rem;
      border-bottom:1px solid rgba(0,212,255,.15);
    }
    .nav-burger { display:flex; }
  }

  /* ── SECTION BASE ── */
  section {
    position:relative; overflow:hidden;
    padding: 7rem 2rem;
  }
  .section-inner { max-width:1100px; margin:0 auto; }

  /* section title */
  .s-title {
    font-family:'Cinzel',serif; font-size:2rem; font-weight:700;
    color: var(--cyanL); letter-spacing:.1em;
    text-shadow: 0 0 20px rgba(0,212,255,.5);
    margin-bottom:.5rem;
  }
  .s-divider {
    width:70px; height:1px;
    background: linear-gradient(90deg,transparent,var(--cyan),transparent);
    box-shadow: 0 0 8px rgba(0,212,255,.5);
    margin-bottom:3rem;
    animation: lineExpand 3s ease-in-out infinite alternate;
  }

  /* ── RUNE BACKGROUND ── */
  .rune-bg {
    position:absolute; inset:0; pointer-events:none; overflow:hidden;
    font-family:'Cinzel',serif; font-size:3.5rem; font-weight:900;
    color: rgba(0,212,255,.06); letter-spacing:.3em; line-height:1.6;
    word-break:break-all; user-select:none;
    animation: runeFlicker 8s ease-in-out infinite;
  }

  /* ── ICE GRID OVERLAY ── */
  .ice-grid {
    position:absolute; inset:0; pointer-events:none;
    background-image:
      linear-gradient(rgba(0,212,255,.025) 1px,transparent 1px),
      linear-gradient(90deg,rgba(0,212,255,.025) 1px,transparent 1px);
    background-size:60px 60px;
  }

  /* ── shop ── */
  .shop {
    min-height:100vh; display:flex; align-items:center; justify-content:center;
    background: linear-gradient(-45deg,#040d18,#0d2a45,#0a3a5c,#062040,#071830,#0e3060,#0a2040);
    background-size:400% 400%;
    animation: bgShift 10s ease infinite;
    padding:0;
  }
  .shop-ice-overlay {
    position:absolute; inset:0;
    background-image:url('/ice.jpg');
    background-size:cover; background-position:center;
    opacity:.03; mix-blend-mode:screen; pointer-events:none;
  }
  .shop-glow {
    position:absolute; top:50%; left:50%;
    width:clamp(280px,55vw,520px); height:clamp(280px,55vw,520px);
    border-radius:50%;
    background:radial-gradient(circle,rgba(0,212,255,.13) 0%,rgba(26,127,193,.07) 45%,transparent 70%);
    animation: pulseRing 2.4s ease-in-out infinite;
    pointer-events:none;
  }
  .shop-ring {
    position:absolute; top:50%; left:50%;
    width:clamp(340px,68vw,640px); height:clamp(340px,68vw,640px);
    border-radius:50%;
    border:1px solid rgba(0,212,255,.18);
    box-shadow:0 0 30px rgba(0,212,255,.12),inset 0 0 30px rgba(0,212,255,.05);
    animation: pulseRing 2.4s ease-in-out infinite .3s;
    pointer-events:none;
  }
  .shop-ring2 {
    position:absolute; top:50%; left:50%;
    width:clamp(440px,82vw,760px); height:clamp(440px,82vw,760px);
    border-radius:50%;
    border:1px solid rgba(0,212,255,.07);
    animation: pulseRing 2.4s ease-in-out infinite .7s;
    pointer-events:none;
  }
  .logo-layer {
    position:absolute; top:50%; left:50%;
    transform:translate(-50%,-50%);
    width:80vw; max-width:500px; aspect-ratio:1;
    background-size:contain; background-repeat:no-repeat; background-position:center;
    opacity:0;
  }
  .layer-1 { background-image:url('/1.png');       animation:glowLoop 1.8s ease-in-out infinite .2s; }
  .layer-2 { background-image:url('/2.png');       animation:glowLoop 1.8s ease-in-out infinite .4s; }
  .layer-3 { background-image:url('/3.png');       animation:glowLoop 1.8s ease-in-out infinite .6s; }
  .layer-4 { background-image:url('/4.png');       animation:glowLoop 1.8s ease-in-out infinite .8s; }
  .layer-5 {
    background-image:url('/innerlogo.png');
    animation:showFade .9s ease-in forwards .3s;
    z-index:4; cursor:pointer;
  }
  .layer-5.locked { cursor:default; pointer-events:none; filter:grayscale(70%) brightness(80%); }
  .shop-bar-track {
    position:absolute; bottom:clamp(28px,6vh,48px); left:50%; transform:translateX(-50%);
    width:clamp(140px,35vw,260px); height:3px;
    background:rgba(255,255,255,.07); border-radius:2px; overflow:hidden; z-index:5;
  }
  .shop-bar-fill {
    height:100%;
    background:linear-gradient(90deg,#1A7FC1,#00D4FF,#A8D8F0);
    border-radius:2px;
    box-shadow:0 0 10px rgba(0,212,255,.7);
    animation:shimBar 8s linear forwards;
  }
  .shop-label {
    position:absolute; bottom:clamp(10px,3.5vh,22px); left:50%; transform:translateX(-50%);
    font-family:'Cinzel',serif; font-size:clamp(.65rem,1.3vw,.8rem);
    font-weight:600; letter-spacing:.3em; color:var(--muted);
    z-index:5; white-space:nowrap; user-select:none;
  }
  .shop-snowflake {
    position:absolute; color:rgba(0,212,255,.4);
    animation:floatFlake 3.5s ease-in-out infinite;
    pointer-events:none; user-select:none;
  }

  /* ── ABOUT ── */
  .about-bg { background:linear-gradient(160deg,#071520 0%,#0d2035 50%,#071828 100%); }
  .about-grid { display:grid; grid-template-columns:1fr 1fr; gap:4rem; align-items:center; }
  @media(max-width:720px){ .about-grid { grid-template-columns:1fr; } }
  .about-info { font-size:.92rem; line-height:2; color:var(--cyanL); }
  .about-info strong { color:var(--cyan); font-weight:600; }
  .about-info a { color:#7fdfff; border-bottom:1px solid rgba(0,212,255,.3); text-decoration:none; transition:.3s; }
  .about-info a:hover { color:#fff; border-color:#fff; text-shadow:0 0 10px rgba(0,212,255,.8); }
  .about-bio { margin-top:2rem; font-size:.95rem; line-height:1.9; color:rgba(168,216,240,.8); }
  .about-img-wrap {
    display:flex; justify-content:center; align-items:center;
    position:relative; cursor:pointer;
  }
  .about-img-ring {
    position:absolute; width:320px; height:320px; border-radius:50%;
    border:1px solid rgba(0,212,255,.2);
    background:radial-gradient(circle,transparent 35%,rgba(0,212,255,.06) 70%,transparent 100%);
    animation:pulseRing 4s ease-in-out infinite;
    pointer-events:none;
  }
  .about-img {
    width:280px; height:280px; object-fit:cover; border-radius:14px;
    border:1px solid rgba(0,212,255,.3);
    box-shadow:0 0 30px rgba(0,212,255,.25),0 0 70px rgba(0,212,255,.08);
    transition:transform .4s, box-shadow .4s;
  }
  .about-img:hover {
    transform:scale(1.04);
    box-shadow:0 0 50px rgba(0,212,255,.45),0 0 100px rgba(0,212,255,.15);
  }

  /* ── HOBBIES ── */
  .hobbies-bg { background:linear-gradient(170deg,#060f1a 0%,#0d2035 40%,#071828 100%); }
  .hobby-grid { display:grid; grid-template-columns:1fr 1fr; gap:3rem; align-items:start; }
  @media(max-width:720px){ .hobby-grid { grid-template-columns:1fr; } }
  .hobby-btns { display:flex; gap:.75rem; margin-bottom:1.5rem; flex-wrap:wrap; }
  .hbtn {
    padding:.55rem 1.2rem; border-radius:30px; cursor:pointer; font-size:1.2rem;
    border:1px solid rgba(0,212,255,.3);
    background:rgba(7,24,40,.7); backdrop-filter:blur(8px);
    transition:all .3s; box-shadow:0 0 8px rgba(0,212,255,.08);
  }
  .hbtn:hover, .hbtn.active {
    background:rgba(0,212,255,.15); border-color:var(--cyan);
    box-shadow:0 0 18px rgba(0,212,255,.4),0 0 40px rgba(0,212,255,.1);
    transform:translateY(-1px);
  }
  .poem-glass {
    background:rgba(7,24,40,.6); backdrop-filter:blur(14px);
    border:1px solid rgba(0,212,255,.18); border-radius:16px;
    padding:2rem; min-height:220px;
    box-shadow:0 0 25px rgba(0,212,255,.07),inset 0 1px 0 rgba(0,212,255,.12);
  }
  .poem-text {
    font-size:.9rem; line-height:2.1; font-style:italic;
    color:rgba(168,216,240,.85); letter-spacing:.03em;
    text-shadow:0 0 8px rgba(0,212,255,.15);
    margin-bottom:1rem; white-space:pre-line;
  }
  .hobby-img-wrap { position:relative; display:flex; justify-content:center; }
  .hobby-img-frame {
    position:relative; display:inline-block;
  }
  .hobby-img {
    width:100%; max-width:340px; height:340px; object-fit:cover; border-radius:12px;
    border:1px solid rgba(0,212,255,.3);
    box-shadow:0 0 30px rgba(0,212,255,.25),0 0 70px rgba(0,212,255,.08);
    display:block;
  }
  .frost-corner { position:absolute; width:18px; height:18px; border-color:rgba(0,212,255,.6); border-style:solid; }
  .fc-tl { top:-3px; left:-3px; border-width:2px 0 0 2px; border-radius:3px 0 0 0; }
  .fc-tr { top:-3px; right:-3px; border-width:2px 2px 0 0; border-radius:0 3px 0 0; }
  .fc-bl { bottom:-3px; left:-3px; border-width:0 0 2px 2px; border-radius:0 0 0 3px; }
  .fc-br { bottom:-3px; right:-3px; border-width:0 2px 2px 0; border-radius:0 0 3px 0; }

  /* ── STAY FROSTY (projects) ── */
  .frosty-bg {
    background:linear-gradient(180deg,#050d18 0%,#071828 50%,#040d18 100%);
    text-align:center;
  }
  .frosty-title {
    font-family:'Cinzel',serif; font-size:3rem; font-weight:900;
    color:var(--white); animation:titlePulse 4s ease-in-out infinite;
    letter-spacing:.1em; margin-bottom:.4rem;
  }
  .frosty-sub { font-size:1.05rem; color:var(--muted); margin-bottom:3rem; letter-spacing:.05em; }
  .frosty-btns { display:flex; gap:1.2rem; justify-content:center; flex-wrap:wrap; }
  .frosty-btn {
    position:relative; overflow:hidden;
    padding:.85rem 2rem; border-radius:30px; cursor:pointer;
    font-family:'Cinzel',serif; font-size:.85rem; font-weight:600;
    letter-spacing:.15em; text-transform:uppercase;
    color:var(--white); text-decoration:none; display:inline-block;
    background:rgba(7,24,40,.7); backdrop-filter:blur(10px);
    border:1px solid rgba(0,212,255,.4);
    box-shadow:0 0 15px rgba(0,212,255,.1);
    transition:all .35s;
  }
  .frosty-btn::before {
    content:''; position:absolute; inset:0;
    background:radial-gradient(circle at 50% 0%,rgba(0,212,255,.2),transparent 70%);
    opacity:0; transition:.3s;
  }
  .frosty-btn:hover::before { opacity:1; }
  .frosty-btn:hover {
    border-color:var(--cyan); color:#fff;
    box-shadow:0 0 25px rgba(0,212,255,.5),0 0 60px rgba(0,212,255,.15);
    transform:translateY(-2px);
    text-shadow:0 0 10px rgba(0,212,255,.9);
  }

  /* ── CONTACT ── */
  .contact-bg { background:linear-gradient(160deg,#060f1a 0%,#0d2035 50%,#071828 100%); }
  .contact-form-wrap {
    max-width:680px; margin:0 auto;
    background:rgba(7,24,40,.6); backdrop-filter:blur(16px);
    border:1px solid rgba(0,212,255,.2); border-radius:20px;
    padding:2.5rem;
    box-shadow:0 0 40px rgba(0,212,255,.07),inset 0 1px 0 rgba(0,212,255,.12);
  }
  .form-row { display:grid; grid-template-columns:1fr 1fr; gap:1.2rem; }
  @media(max-width:580px){ .form-row { grid-template-columns:1fr; } }
  .fg { display:flex; flex-direction:column; gap:.4rem; margin-bottom:1.2rem; }
  .fg label {
    font-size:.75rem; font-weight:600; letter-spacing:.12em; text-transform:uppercase;
    color:rgba(0,212,255,.7); text-shadow:0 0 6px rgba(0,212,255,.3);
  }
  .fg input, .fg textarea {
    padding:.7rem 1rem; border-radius:8px; outline:none;
    border:1px solid rgba(0,212,255,.22);
    background:rgba(4,13,24,.85); color:var(--white);
    font-family:'Raleway',sans-serif; font-size:.95rem;
    transition:all .3s;
  }
  .fg input::placeholder, .fg textarea::placeholder { color:rgba(0,212,255,.25); }
  .fg input:focus, .fg textarea:focus {
    border-color:rgba(0,212,255,.65);
    box-shadow:0 0 15px rgba(0,212,255,.18),inset 0 0 8px rgba(0,212,255,.05);
    background:rgba(7,24,40,.95);
  }
  .fg textarea { resize:vertical; min-height:130px; }
  .submit-btn {
    width:100%; padding:.9rem; border-radius:8px; cursor:pointer;
    font-family:'Cinzel',serif; font-size:.85rem; font-weight:700;
    letter-spacing:.15em; text-transform:uppercase;
    color:var(--white); border:1px solid rgba(0,212,255,.5);
    background:rgba(0,212,255,.12); backdrop-filter:blur(8px);
    transition:all .35s; position:relative; overflow:hidden;
    box-shadow:0 0 15px rgba(0,212,255,.12);
  }
  .submit-btn::before {
    content:''; position:absolute; inset:0;
    background:linear-gradient(135deg,rgba(0,212,255,.1),transparent 50%,rgba(0,212,255,.05));
    pointer-events:none;
  }
  .submit-btn:hover:not(:disabled) {
    background:rgba(0,212,255,.22); border-color:var(--cyan);
    box-shadow:0 0 25px rgba(0,212,255,.45),0 0 60px rgba(0,212,255,.12);
    text-shadow:0 0 10px rgba(0,212,255,.9); transform:translateY(-1px);
  }
  .submit-btn:disabled { opacity:.5; cursor:not-allowed; }
  .msg-ok  { color:var(--cyan); font-weight:600; text-align:center; margin-top:1rem; text-shadow:0 0 8px rgba(0,212,255,.5); }
  .msg-err { color:#ff6b6b;     font-weight:600; text-align:center; margin-top:1rem; }

  /* ── FOOTER ── */
  footer {
    background:linear-gradient(180deg,#040d18,#020810);
    border-top:1px solid rgba(0,212,255,.15);
    padding:2rem;
    display:flex; align-items:center; justify-content:space-between; flex-wrap:wrap; gap:1rem;
  }
  .footer-logo {
    height:80px; object-fit:contain;
    filter:drop-shadow(0 0 8px rgba(0,212,255,.4)) drop-shadow(0 0 20px rgba(0,212,255,.2));
    transition:.3s;
  }
  .footer-logo:hover { filter:drop-shadow(0 0 18px rgba(0,212,255,.9)) drop-shadow(0 0 40px rgba(0,212,255,.5)); }
  .footer-copy { font-size:.8rem; color:rgba(0,212,255,.5); letter-spacing:.08em; }
  .soc-icons { display:flex; gap:.75rem; }
  .soc-icon {
    width:40px; height:40px; border-radius:50%; display:inline-flex;
    align-items:center; justify-content:center;
    background:rgba(7,24,40,.8); color:var(--cyanL);
    border:1px solid rgba(0,212,255,.28); font-size:1.1rem;
    text-decoration:none; transition:all .3s;
    box-shadow:0 0 8px rgba(0,212,255,.08);
  }
  .soc-icon:hover {
    background:rgba(0,212,255,.15); border-color:var(--cyan);
    color:#fff; box-shadow:0 0 18px rgba(0,212,255,.45);
    transform:translateY(-2px);
  }

  /* ── MODAL ── */
  .modal-overlay {
    position:fixed; inset:0; z-index:9999;
    background:rgba(2,8,16,.88); backdrop-filter:blur(12px);
    display:flex; align-items:center; justify-content:center;
    animation:fadeUp .25s ease;
  }
  .modal-box {
    background:linear-gradient(145deg,#0d2030,#1a3a50);
    border:1px solid rgba(0,212,255,.35); border-radius:18px;
    padding:2.5rem; max-width:440px; width:90%; text-align:center; position:relative;
    box-shadow:0 0 50px rgba(0,212,255,.18),0 0 100px rgba(0,212,255,.07),
               inset 0 1px 0 rgba(0,212,255,.18);
    animation:modalIn .35s cubic-bezier(.34,1.56,.64,1);
    overflow:hidden;
  }
  .modal-ring {
    position:absolute; top:-60px; left:50%; transform:translateX(-50%);
    width:260px; height:260px; border-radius:50%;
    background:radial-gradient(circle,rgba(0,212,255,.07),transparent 70%);
    pointer-events:none;
  }
  .modal-poem {
    position:relative; z-index:1; font-style:italic; white-space:pre-line;
    line-height:1.95; color:rgba(168,216,240,.9); font-size:.9rem;
    letter-spacing:.02em; text-shadow:0 0 6px rgba(0,212,255,.12);
  }
  .modal-btn {
    position:relative; overflow:hidden; z-index:1;
    margin-top:1.5rem; padding:.6rem 2.2rem;
    border-radius:30px; cursor:pointer;
    font-family:'Cinzel',serif; font-size:.8rem; font-weight:700;
    letter-spacing:.15em; color:var(--white);
    background:rgba(0,212,255,.12); border:1px solid rgba(0,212,255,.45);
    box-shadow:0 0 12px rgba(0,212,255,.12); transition:all .3s;
  }
  .modal-btn::after {
    content:''; position:absolute; top:0; left:-100%; width:60%; height:100%;
    background:linear-gradient(90deg,transparent,rgba(255,255,255,.14),transparent);
    transition:left .5s;
  }
  .modal-btn:hover::after { left:150%; }
  .modal-btn:hover {
    background:rgba(0,212,255,.22); border-color:var(--cyan);
    box-shadow:0 0 20px rgba(0,212,255,.45);
    text-shadow:0 0 8px rgba(0,212,255,.9);
  }

  /* fade-up for sections */
  .reveal { opacity:0; transform:translateY(30px); transition:opacity .7s ease, transform .7s ease; }
  .reveal.visible { opacity:1; transform:translateY(0); }
`;

/* ─── COMPONENT ────────────────────────────────────────────────── */
export default function Portfolio() {
  const [menuOpen, setMenuOpen]       = useState(false);
  const [clickable, setClickable]     = useState(false);
  const [hobby, setHobby]             = useState("robotics");
  const [modal, setModal]             = useState(false);
  const [imgSrc, setImgSrc]           = useState("/majadpic.jpg");
  const [form, setForm]               = useState({ name:"",email:"",subject:"",message:"" });
  const [loading, setLoading]         = useState(false);
  const [ok, setOk]                   = useState("");
  const [err, setErr]                 = useState("");

  // inject styles once
  useEffect(() => {
    const el = document.createElement("style");
    el.textContent = css;
    document.head.appendChild(el);
    return () => document.head.removeChild(el);
  }, []);

  // shop clickable timer
  useEffect(() => {
    const t = setTimeout(() => setClickable(true), 8000);
    return () => clearTimeout(t);
  }, []);

  // scroll reveal
  useEffect(() => {
    const obs = new IntersectionObserver((entries) => {
      entries.forEach(e => { if (e.isIntersecting) e.target.classList.add("visible"); });
    }, { threshold: 0.12 });
    document.querySelectorAll(".reveal").forEach(el => obs.observe(el));
    return () => obs.disconnect();
  }, []);

  // modal -> swap image
  const closeModal = () => { setModal(false); setImgSrc("/majadpic.jpg"); };

  const sendMsg = async (e) => {
    e.preventDefault();
    setLoading(true); setOk(""); setErr("");
    try {
      const res = await fetch(`https://majad-backend.onrender.com/api/contact`, {
        method:"POST", headers:{"Content-Type":"application/json"},
        body:JSON.stringify(form),
      });
      const data = await res.json();
      if (!res.ok) { setErr(data.errors?.map(e=>e.msg).join(", ") || data.message || "❌ Failed."); return; }
      setOk(data.message || "✅ Message sent!");
      setForm({ name:"",email:"",subject:"",message:"" });
    } catch { setErr("❌ Network error. Try again."); }
    finally { setLoading(false); }
  };

  const RUNES = "ᚠᚢᚦᚨᚱᚲᚷᚹᚺᚾᛁᛃᛇᛈᛉᛊᛏᛒᛖᛗᛚᛜᛞᛟ";

  return (
    <>
      {/* ── NAVBAR ── */}
      <nav className="nav">
        <a className="nav-logo" href="#shop">✦ MAJAD</a>
        <ul className={`nav-links${menuOpen ? " open" : ""}`}>
          {["shop","about","hobbies","frosty","contact"].map(id => (
            <li key={id}><a href={`#${id}`} onClick={() => setMenuOpen(false)}>
              {id === "frosty" ? "Journey" : id.charAt(0).toUpperCase() + id.slice(1)}
            </a></li>
          ))}
        </ul>
        <div className="nav-burger" onClick={() => setMenuOpen(o => !o)}>
          <span/><span/><span/>
        </div>
      </nav>

      {/* ── shop ── */}
      <section className="shop" id="shop">
        <div className="shop-ice-overlay"/>
        <div className="shop-glow"/>
        <div className="shop-ring"/>
        <div className="shop-ring2"/>
        {/* runic bg */}
        <div className="rune-bg" style={{padding:"3rem",zIndex:0}}>
          {Array.from({length:12},(_,i) =>
            <div key={i}>{RUNES.repeat(8).split("").sort(()=>Math.random()-.5).join(" ")}</div>
          )}
        </div>
        {/* snowflakes */}
        {SNOWFLAKES.map((f,i) => (
          <span key={i} className="shop-snowflake" style={{
            left:f.left, top:f.top, fontSize:f.size,
            animationDelay:f.delay, animationDuration:f.dur,
          }}>❄</span>
        ))}
        {/* logo layers */}
        <div className="logo-layer layer-1"/>
        <div className="logo-layer layer-2"/>
        <div className="logo-layer layer-3"/>
        <div className="logo-layer layer-4"/>
        <div
          className={`logo-layer layer-5${clickable ? "" : " locked"}`}
          onClick={() => clickable && window.open("https://majad-shop.vercel.app","_blank")}
        />
        {/* bar */}
        <div className="shop-bar-track"><div className="shop-bar-fill"/></div>
        <p className="shop-label">✦ &nbsp; M A JA D &nbsp; S H O P &nbsp; ✦</p>
      </section>

      {/* ── ABOUT ── */}
      <section className="about-bg" id="about">
        <div className="ice-grid"/>
        <div className="rune-bg" style={{opacity:.4}}>
          {Array.from({length:6},(_,i)=><div key={i}>{RUNES.repeat(6)}</div>)}
        </div>
        <div className="section-inner reveal">
          <h2 className="s-title">About Me</h2>
          <div className="s-divider"/>
          <div className="about-grid">
            <div>
              <p className="about-info">
                <strong>Name:</strong> Angel Jesse Dampil<br/>
                <strong>Birthday:</strong> December 7, 1999<br/>
                <strong>Age:</strong> 24 years old<br/>
                <strong>Address:</strong> Halang, Taal, Batangas<br/>
                <strong>Phone:</strong> 0949 079 9760<br/>
                <strong>Email:</strong> ajdampil01@gmail.com<br/>
                <strong>Website:</strong> <a href="https://majad.jimdosite.com" target="_blank" rel="noopener noreferrer">majad.jimdosite.com</a><br/>
                <strong>Linktree:</strong> <a href="https://linktr.ee/ajdampil01" target="_blank" rel="noopener noreferrer">linktr.ee/ajdampil01</a>
              </p>
              <div className="about-bio">
                <p style={{marginBottom:"1rem"}}>
                  I'm an Asian statistician and data enthusiast with a strong foundation in mathematical logic
                  and real-world analytics. My experience spans from building predictive models to performing
                  accurate forecasts using R and SQL.
                </p>
                <p style={{marginBottom:"1rem"}}>
                  I genuinely enjoy the craft of statistics — the challenge of making sense of uncertainty,
                  the thrill of finding connections others might miss, and the satisfaction of turning data
                  into decisions. My goal is to become one of the best statisticians in my field.
                </p>
                <p>
                  Looking ahead, I'm committed to pushing my expertise further through advanced studies in
                  MS and PhD programs in statistics and data science, contributing to both academia and
                  impactful real-world projects.
                </p>
              </div>
            </div>
            <div className="about-img-wrap" onClick={() => setModal(true)}>
              <div className="about-img-ring"/>
              <img src={imgSrc} alt="Majad" className="about-img"/>
            </div>
          </div>
        </div>
      </section>

      {/* ── HOBBIES ── */}
      <section className="hobbies-bg" id="hobbies">
        <div className="ice-grid"/>
        <div className="section-inner reveal">
          <h2 className="s-title">My Hobbies</h2>
          <div className="s-divider"/>
          <div className="hobby-btns">
            {[{v:"robotics",i:"🤖"},{v:"violin",i:"🎻"},{v:"racing",i:"🏎️"}].map(h => (
              <button key={h.v} className={`hbtn${hobby===h.v?" active":""}`} onClick={()=>setHobby(h.v)}>
                {h.i}
              </button>
            ))}
          </div>
          <div className="hobby-grid">
            <div>
              <div className="poem-glass">
                {POEMS[hobby].map((stanza, i) => (
                  <p key={i} className="poem-text">{stanza}</p>
                ))}
              </div>
            </div>
            <div className="hobby-img-wrap">
              <div className="hobby-img-frame">
                <img
                  src={`/${hobby}.jpg`}
                  alt={hobby}
                  className="hobby-img"
                />
                <div className="frost-corner fc-tl"/>
                <div className="frost-corner fc-tr"/>
                <div className="frost-corner fc-bl"/>
                <div className="frost-corner fc-br"/>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── STAY FROSTY ── */}
      <section className="frosty-bg" id="frosty">
        <div className="ice-grid"/>
        {/* animated snowflakes */}
        {Array.from({length:18},(_,i) => {
          const left = ((i*137.5)%100).toFixed(1);
          const delay = (i*0.6%8).toFixed(1);
          const dur   = (6+(i*1.2%7)).toFixed(1);
          const size  = (.6+(i*0.15%0.9)).toFixed(2);
          return (
            <span key={i} style={{
              position:"absolute", left:`${left}%`, top:"-25px",
              fontSize:`${size}rem`, color:"rgba(0,212,255,.35)",
              animation:`snowDrift ${dur}s linear ${delay}s infinite`,
              zIndex:1, pointerEvents:"none", userSelect:"none",
            }}>❄</span>
          );
        })}
        <div className="section-inner reveal" style={{position:"relative",zIndex:2}}>
          <h2 className="frosty-title">Stay Frosty!</h2>
          <div className="s-divider" style={{margin:"0 auto 1rem"}}/>
          <p className="frosty-sub">Links about my journey.</p>
          <div className="frosty-btns">
            {[
              { label:"Rpubs",           href:"https://rpubs.com/Majad07" },
              { label:"Datacamp",        href:"https://www.datacamp.com/portfolio/ajdampil01" },
              { label:"Aj's Certificates",href:"https://drive.google.com/drive/folders/1tDmgcsSFpGp05T_UMgL_PUfy9tHV5P8C?usp=sharing" },
            ].map(b => (
              <a key={b.label} href={b.href} target="_blank" rel="noopener noreferrer" className="frosty-btn">
                {b.label}
              </a>
            ))}
          </div>
        </div>
      </section>

      {/* ── CONTACT ── */}
      <section className="contact-bg" id="contact">
        <div className="ice-grid"/>
        <div className="rune-bg" style={{opacity:.35}}>
          {Array.from({length:6},(_,i)=><div key={i}>{RUNES.repeat(6)}</div>)}
        </div>
        <div className="section-inner reveal">
          <h2 className="s-title">Contact Me</h2>
          <div className="s-divider"/>
          <div className="contact-form-wrap">
            <form onSubmit={sendMsg}>
              <div className="form-row">
                <div className="fg">
                  <label>Full Name</label>
                  <input value={form.name} onChange={e=>setForm({...form,name:e.target.value})} required placeholder="Your name"/>
                </div>
                <div className="fg">
                  <label>Email</label>
                  <input type="email" value={form.email} onChange={e=>setForm({...form,email:e.target.value})} required placeholder="your@email.com"/>
                </div>
              </div>
              <div className="fg">
                <label>Subject</label>
                <input value={form.subject} onChange={e=>setForm({...form,subject:e.target.value})} required placeholder="What's this about?"/>
              </div>
              <div className="fg">
                <label>Message</label>
                <textarea value={form.message} onChange={e=>setForm({...form,message:e.target.value})} required placeholder="Your message..." rows={5}/>
              </div>
              <button type="submit" className="submit-btn" disabled={loading}>
                {loading ? "Sending..." : "✦ Send Message ✦"}
              </button>
              {ok  && <p className="msg-ok">{ok}</p>}
              {err && <p className="msg-err">{err}</p>}
            </form>
          </div>
        </div>
      </section>

      {/* ── FOOTER ── */}
      <footer>
        <img src="/innerlogo.png" alt="MAJAD Logo" className="footer-logo"/>
        <span className="footer-copy">© {new Date().getFullYear()} MAJAD. All rights reserved.</span>
        <div className="soc-icons">
          <a href="https://www.facebook.com/aj.dampil.9?mibextid=ZbWKwL" rel="noopener noreferrer" target="_blank" className="soc-icon">
            <i className="bi bi-facebook"/>
          </a>
          <a href="https://www.instagram.com/dajd07/" rel="noopener noreferrer" target="_blank" className="soc-icon">
            <i className="bi bi-instagram"/>
          </a>
        </div>
      </footer>

      {/* ── MODAL ── */}
      {modal && (
        <div className="modal-overlay" onClick={closeModal}>
          <div className="modal-box" onClick={e => e.stopPropagation()}>
            <div className="modal-ring"/>
            <p className="modal-poem">{MODAL_POEM}</p>
            <button className="modal-btn" onClick={closeModal}>Okay</button>
          </div>
        </div>
      )}
    </>
  );
}